import React, { useState } from 'react'
import Checkbox from '@mui/material/Checkbox'
import Popup from '../../components/Popup'
import { $moment } from '../../funcs'
import { applyDiscountToPrice } from '../../util/priceCalculation'
import { ReactComponent as CheckboxIcon } from '../Icons/size24/Checkbox.svg'
import { ReactComponent as CheckboxChecked } from '../Icons/size24/CheckboxChecked.svg'

type TariffLike = {
    type?: string
    period?: string
    price?: number
    discount?: number
}

type Props = {
    onClose: () => void
    tariff: TariffLike
    $store?: any
    subscribe: () => void
}

function subscriptionPeriod(period?: string): string {
    if (period === 'month') return 'месяц'
    if (period === 'quarter') return '3 месяца'
    if (period === 'year') return 'год'
    if (period === '11months') return '11 месяцев'
    return 'период'
}

function chargeDate(period?: string): string {
    if (period === 'month') {
        return `Оплата будет списываться ${$moment().format('D')} числа каждого месяца`
    }
    if (period === 'quarter') {
        return `Следующее списание произойдет ${$moment().add(3, 'M').format('LL')}`
    }
    if (period === 'year') {
        return `Следующее списание произойдет ${$moment().add(1, 'y').format('D MMMM')} следующего года`
    }
    if (period === '11months') {
        return `Следующее списание произойдет ${$moment().add(10, 'M').format('LL')}`
    }
    return 'Оплата будет списываться автоматически в соответствии с выбранным периодом'
}

function switchTariffRule(tariff: TariffLike, userSession?: any): string {
    if (tariff.type === 'pro_subscription') {
        if (userSession?.is_pro_subscription) {
            return 'При оплате срок действия подписки Pro будет продлен.'
        }
        return 'После оплаты вы сможете настроить favicon, логотип и цвет на странице подписания для клиентов.'
    }

    const myTariffIsSubscription = userSession?.tariff?.type === 'subscription'
    if (myTariffIsSubscription) {
        return 'Остаток действия текущей подписки будет учтен при переходе на новую подписку.'
    }
    return 'Внимание! При переходе на безлимитную подписку оставшийся баланс договоров будет утерян и его невозможно будет восстановить!'
}

function serviceIntro(tariff: TariffLike): string {
    if (tariff.type === 'pro_subscription') {
        return 'Вы собираетесь оформить подписку Pro — брендирование страницы подписания для ваших клиентов. Подписка Pro не включает пакет документов: для отправки договоров нужен отдельный тариф. Мы просим вас ознакомиться с несколькими важными условиями:'
    }
    return 'Вы собираетесь оформить подписку для безлимитного доступа к нашему сервису. Мы просим вас ознакомиться с несколькими важными условиями:'
}

function popupTitle(tariff: TariffLike): string {
    return tariff.type === 'pro_subscription' ? 'Оформление подписки Pro' : 'Оформление подписки'
}

function confirmButtonLabel(tariff: TariffLike): string {
    return tariff.type === 'pro_subscription' ? 'Продолжить к оплате' : 'Оформить подписку'
}

function SubscriptionPopup({ onClose, tariff, $store, subscribe }: Props) {
    const [termChecked, setTermsChecked] = useState(false)
    const [agreementChecked, setAgreementChecked] = useState(false)
    const periodLabel = subscriptionPeriod(tariff.period)
    const discount = tariff.discount || 0
    const priceRub = applyDiscountToPrice(tariff.price || 0, discount) / 100

    return (
        <Popup style={{ zIndex: 999 }} onClose={onClose}>
            <div>
                <div className="commonLabel">{popupTitle(tariff)}</div>

                <div className="commonSubtitle" style={{ marginTop: '25px' }}>
                    {serviceIntro(tariff)}
                    <br />
                    <br />
                    1. Подписка оформляется на {periodLabel}. Стоимость подписки — {priceRub} рублей за {periodLabel}. {chargeDate(tariff.period)}.
                    <br />
                    <br />
                    <b>{switchTariffRule(tariff, $store?.userSession)}</b>
                    <br />
                    <br />
                    2. Оферта содержит описание условий автоплатежа. Платежи будут списываться безакцептно с вашей карты в соответствии с выбранным периодом.
                    <br />
                    <br />
                    3. Согласие на обработку персональных данных. Мы обязуемся защищать и обрабатывать ваши персональные данные с соблюдением законодательства о конфиденциальности.
                    <br />
                    <br />
                    4. Согласие на сохранение учетных данных. Для проведения будущих транзакций мы просим ваше согласие на сохранение платёжных данных для автопродления подписки.
                    <br />
                    <br />
                    5. Правила отмены. Вы можете отменить автопродление подписки в профиле или в настройках оформления страницы договора.
                </div>

                <hr className="newHR" />

                <div className="buttonsContainer">
                    <Checkbox
                        icon={<CheckboxIcon className="icon" />}
                        checkedIcon={<CheckboxChecked className="icon" />}
                        checked={termChecked}
                        onChange={({ target: { checked } }) => setTermsChecked(checked)}
                    />
                    <div className="commonSubtitle">
                        Я даю согласие на обработку персональных данных в соответствии с{' '}
                        <a href="/confidential_policy.pdf" target="_blank" rel="noreferrer">Политикой конфиденциальности</a>{' '}
                        и принимаю условия{' '}
                        <a href="/agreement.pdf" target="_blank" rel="noreferrer">Соглашения</a>
                    </div>
                </div>

                <div className="buttonsContainer" style={{ marginTop: '15px' }}>
                    <Checkbox
                        icon={<CheckboxIcon className="icon" />}
                        checkedIcon={<CheckboxChecked className="icon" />}
                        checked={agreementChecked}
                        onChange={({ target: { checked } }) => setAgreementChecked(checked)}
                    />
                    <div className="commonSubtitle">
                        Я согласен с условиями платной подписки, изложенными в{' '}
                        <a href="/agreement_subscriptions.pdf" target="_blank" rel="noreferrer">Соглашении об использовании платной подписки</a>,{' '}
                        и даю{' '}
                        <a href="/agreement_transactions.pdf" target="_blank" rel="noreferrer">согласие на сохранение учетных данных для будущих транзакций</a>
                    </div>
                </div>

                <hr className="newHR" />

                <button
                    disabled={!termChecked || !agreementChecked}
                    className="new withTitle _wa"
                    onClick={subscribe}
                >
                    {confirmButtonLabel(tariff)}
                </button>
            </div>
        </Popup>
    )
}

export default SubscriptionPopup
export { SubscriptionPopup }
